import { useEffect, useMemo, useState } from 'react';
import { CallStatus, useCallable } from '../../lib/useCallable';
import { useDoc, useFormConfig } from '../../lib/useFirestore';
import { FORM_SECTIONS } from '../../lib/formSchema';
import { buildEffectiveSections } from '../../lib/fieldLayout';
import { sortPeople } from '../../lib/sortPeople';

/**
 * Keyword alerts: tell named people when a submitted matter says a particular thing.
 *
 * WHAT THIS IS NOT. It is not an approval and cannot become one. Recipients resolve to ADDRESSES
 * on the server (approval/recipients.js), never to a person who can act, so nothing configured
 * here mints a magic link, gates a matter, or appears in the audit trail as having approved
 * anything. It is worth saying plainly on the page, because "notify the General Counsel when
 * somebody writes X" reads a lot like "route it to the General Counsel" and they are very
 * different things.
 *
 * THE FIELD LIST IS THE LIVE FORM'S. It comes from the same merge the intake form renders from,
 * so it offers the questions people can actually answer — under the firm's own labels — including
 * ones an administrator added. A hand-kept list of paths would be wrong the first time somebody
 * edited the form, which has happened twice on this project already.
 *
 * WHOLE WORDS ARE THE DEFAULT and the page says why. A rule watching for "PAGA" that fires on
 * "propaganda" gets muted within a week, and a muted alert is worse than none: it is a control
 * everybody believes is on.
 */

const field = 'w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm';
const RECIPIENT_KINDS = [
  ['managing_partner', 'Managing Partner'],
  ['general_counsel', 'General Counsel'],
  ['practice_group_leader', 'Practice Group Leader'],
  ['responsible_partner', 'Responsible Partner'],
  ['team_leader', 'Team Leader'],
  ['user', 'A specific person'],
  ['email', 'An email address'],
];

/** Keywords are stored as a list and edited as lines, converted at save rather than per keystroke. */
const linesToList = (text) =>
  String(text || '')
    .split(/[\n,]/)
    .map((k) => k.trim())
    .filter(Boolean);

function RuleCard({ rule, fields, roster, onPatch, onRemove, onMove }) {
  const [keywordText, setKeywordText] = useState((rule.keywords || []).join('\n'));

  // Kept in local state so a trailing space or a half-typed keyword survives; the list is
  // rebuilt on every change, but what the box SHOWS is what was typed.
  useEffect(() => {
    onPatch({ keywords: linesToList(keywordText) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keywordText]);

  const paths = rule.fieldPaths || [];
  const toggleField = (path) =>
    onPatch({ fieldPaths: paths.includes(path) ? paths.filter((p) => p !== path) : [...paths, path] });

  const patchRecipient = (index, changes) =>
    onPatch({ recipients: (rule.recipients || []).map((r, i) => (i === index ? { ...r, ...changes } : r)) });

  return (
    <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
      <div className="flex items-center gap-3">
        <input
          className={`${field} font-medium`}
          value={rule.label || ''}
          placeholder="What this alert is called, e.g. Potential class action"
          onChange={(e) => onPatch({ label: e.target.value })}
        />
        <label className="flex shrink-0 items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={rule.isActive !== false}
            onChange={(e) => onPatch({ isActive: e.target.checked })}
          />
          On
        </label>
      </div>

      <div className="mt-3 grid gap-4 md:grid-cols-2">
        <div>
          <label className="block text-xs text-slate-600 dark:text-slate-300">
            Words and phrases to watch for
            <textarea
              className={`${field} mt-1`}
              rows={4}
              value={keywordText}
              placeholder={'class action\nPAGA\nwhistleblower'}
              onChange={(e) => setKeywordText(e.target.value)}
            />
          </label>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            One per line, or separated by commas. A phrase is fine, and there is no need to put
            quotation marks around it — if you do, they are ignored rather than searched for.
          </p>

          <label className="mt-3 flex items-start gap-2 text-xs text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={rule.wholeWord !== false}
              onChange={(e) => onPatch({ wholeWord: e.target.checked })}
            />
            <span>
              Whole words only
              <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                Recommended. Without it, “PAGA” fires on “propaganda” — and an alert that cries wolf
                gets filtered to a folder, which is worse than not having it.
              </span>
            </span>
          </label>
          <label className="mt-2 flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              checked={rule.caseSensitive === true}
              onChange={(e) => onPatch({ caseSensitive: e.target.checked })}
            />
            Match upper and lower case exactly
          </label>
          <label className="mt-2 flex items-start gap-2 text-xs text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={rule.includePdf === true}
              onChange={(e) => onPatch({ includePdf: e.target.checked })}
            />
            <span>
              Attach the matter PDF
              <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                Off by default: the alert already quotes the text that matched, and attaching the
                whole record sends the firm’s full intake to every address on this rule.
              </span>
            </span>
          </label>
        </div>

        <div>
          <p className="text-xs text-slate-600 dark:text-slate-300">Which questions to search</p>
          <div className="mt-1 max-h-44 overflow-y-auto rounded border border-slate-200 p-2 dark:border-slate-700">
            {fields.map((f) => (
              <label key={f.path} className="flex items-start gap-2 py-0.5 text-[12px] text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={paths.includes(f.path)}
                  onChange={() => toggleField(f.path)}
                />
                <span>
                  {f.label}
                  <span className="block text-[10.5px] text-slate-400">{f.sectionTitle}</span>
                </span>
              </label>
            ))}
          </div>
          {/*
            The chosen questions are NAMED, not counted. "Searching 2 questions" is what the first
            live rule said while searching the adverse-party tables for a phrase that was in the
            client's name — a count tells you the rule is configured, not whether it is pointed at
            the right place.
          */}
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            {paths.length ? (
              <>
                Searching:{' '}
                {paths
                  .map((path) => (fields.find((f) => f.path === path) || {}).label || path)
                  .join(', ')}
                .
              </>
            ) : (
              'Nothing ticked — every answer on the matter is searched, which is usually what you want.'
            )}
          </p>

          <p className="mt-3 text-xs text-slate-600 dark:text-slate-300">Who to tell</p>
          {(rule.recipients || []).map((recipient, index) => (
            <div key={index} className="mt-1 flex items-center gap-2">
              <select
                className={field}
                value={recipient.kind || 'email'}
                onChange={(e) => patchRecipient(index, { kind: e.target.value })}
              >
                {RECIPIENT_KINDS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {recipient.kind === 'email' && (
                <input
                  className={field}
                  type="email"
                  placeholder="someone@aalrr.com"
                  value={recipient.email || ''}
                  onChange={(e) => patchRecipient(index, { email: e.target.value })}
                />
              )}
              {recipient.kind === 'user' && (
                <select
                  className={field}
                  value={recipient.uid || ''}
                  onChange={(e) => patchRecipient(index, { uid: e.target.value })}
                >
                  <option value="">Choose…</option>
                  {sortPeople(roster).map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.displayName || person.email}
                    </option>
                  ))}
                </select>
              )}
              <button
                type="button"
                onClick={() => onPatch({ recipients: rule.recipients.filter((_, i) => i !== index) })}
                className="shrink-0 text-xs text-red-600 underline dark:text-red-400"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => onPatch({ recipients: [...(rule.recipients || []), { kind: 'email', email: '' }] })}
            className="mt-1 text-xs text-firm-700 underline dark:text-firm-300"
          >
            + Add somebody
          </button>
        </div>
      </div>

      <label className="mt-3 block text-xs text-slate-600 dark:text-slate-300">
        A line of context for the people who receive it (optional)
        <input
          className={`${field} mt-1`}
          value={rule.note || ''}
          placeholder="e.g. Please review before the engagement letter goes out."
          onChange={(e) => onPatch({ note: e.target.value })}
        />
      </label>

      <div className="mt-3 flex gap-3 text-xs text-slate-500 dark:text-slate-400">
        <button type="button" onClick={() => onMove(-1)} className="underline">
          Move up
        </button>
        <button type="button" onClick={() => onMove(1)} className="underline">
          Move down
        </button>
        <button type="button" onClick={onRemove} className="ml-auto text-red-700 underline dark:text-red-300">
          Delete this alert
        </button>
      </div>
    </div>
  );
}

export default function AdminKeywordAlerts() {
  const { data: stored, loading } = useDoc('settings/keywordAlerts');
  const config = useFormConfig();
  const { run, busy, error, saved } = useCallable();
  const [rules, setRules] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) setRules(Array.isArray(stored?.rules) ? structuredClone(stored.rules) : []);
  }, [stored, dirty]);

  // The live form's questions, under the firm's own labels — the same merge the form renders from.
  const fields = useMemo(() => {
    if (config.loading) return [];
    return buildEffectiveSections(FORM_SECTIONS, config.fieldLayout).flatMap((section) =>
      (section.fields || [])
        .filter((f) => f.path && f.type !== 'notice' && f.type !== 'attachments')
        .map((f) => ({ path: f.path, label: f.label || f.path, sectionTitle: section.title }))
    );
  }, [config.fieldLayout, config.loading]);

  if (loading || !rules) return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;

  const patch = (next) => {
    setDirty(true);
    setRules(next);
  };
  const patchRule = (id, changes) => patch(rules.map((r) => (r.id === id ? { ...r, ...changes } : r)));
  const move = (index, direction) => {
    const next = [...rules];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    patch(next);
  };

  const save = async () => {
    const result = await run('adminUpdateSettings', {
      docId: 'keywordAlerts',
      // adminUpdateSettings REPLACES the document, so the whole list goes every time.
      value: { rules },
    });
    if (result) setDirty(false);
  };

  return (
    <div className="space-y-4">
      <section className="rounded-lg bg-white p-4 shadow dark:bg-slate-900">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Keyword alerts</h2>
          <CallStatus busy={busy} error={error} saved={saved} />
        </div>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Tell named people when a submitted matter says a particular thing — a phrase in the
          description, a party’s name, anything written on the form.
        </p>
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          <strong>These are notifications, not approvals.</strong> An alert never gates a matter,
          never asks anybody to sign anything, and never appears in the approval trail. Nothing here
          can stop or delay a submission — if an alert cannot be sent, the matter still goes through
          and the failure is logged.
        </p>
      </section>

      {rules.length === 0 ? (
        <p className="rounded-lg bg-white p-6 text-sm text-slate-500 shadow dark:bg-slate-900 dark:text-slate-400">
          No alerts yet.
        </p>
      ) : (
        <div className="space-y-3">
          {rules.map((rule, index) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              fields={fields}
              roster={config.roster || []}
              onPatch={(changes) => patchRule(rule.id, changes)}
              onRemove={() => patch(rules.filter((r) => r.id !== rule.id))}
              onMove={(direction) => move(index, direction)}
            />
          ))}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={() =>
            patch([
              ...rules,
              {
                id: `alert_${Date.now().toString(36)}`,
                label: '',
                keywords: [],
                fieldPaths: [],
                recipients: [],
                wholeWord: true,
                caseSensitive: false,
                includePdf: false,
                isActive: true,
                note: '',
              },
            ])
          }
          className="rounded border border-slate-300 px-4 py-2 text-sm dark:border-slate-600"
        >
          + Add an alert
        </button>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="rounded bg-firm-700 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 disabled:opacity-50 dark:bg-firm-500 dark:hover:bg-firm-400"
        >
          Save alerts
        </button>
      </div>
    </div>
  );
}
